import * as React from 'react';
import {
  Droppable,
  DroppableProvided,
  DroppableStateSnapshot,
  Draggable,
  DraggableProvided,
  DraggableProvidedDragHandleProps,
  DraggableRubric,
  DraggableStateSnapshot,
} from '@hello-pangea/dnd';
import { IGroupRule } from './rule-interface';
import { RuleFactory } from './rule-factory';
import { IRuleProps } from './rule-props';
import { IGroupRulesProps } from './fields-and-types';
import { useQueryBuilderContext } from './query-builder-context';
import { isNestedRule } from './rule-functions';
import { DragCloneProvider, isDropTargetDisabled, isDropTargetForbidden, useIsRenderedInDragClone, useQueryBuilderDragState } from './query-builder-drag-state';

function ConditionSelector<T extends IGroupRule>(props: IGroupRulesProps<T>) {
  const context = useQueryBuilderContext();
  const {
    value: { condition },
  } = props;
  const { conditions, controls, translations, classNames } = context;

  const handleConditionChange = (value: string) => {
    context.queryDispatch({ type: 'updateRule', oldRule: props.value, newRule: { ...props.value, condition: value } });
  };

  return (
    <div className="groupRule__conditionSelector">
      {React.createElement(controls.conditionSelector, {
        field: '',
        options: conditions,
        value: condition,
        title: translations.conditions.title,
        className: classNames.conditions,
        handleOnChange: handleConditionChange,
      })}
    </div>
  );
}

export function GroupRules<T extends IGroupRule>(props: IGroupRulesProps<T>) {
  const { resultType, path, havingType } = props;
  const {
    value: { rules },
    fieldsAndTypes: { fields, types },
  } = props;
  const isRenderedInDragClone = useIsRenderedInDragClone();
  const canDragRules = !!path && !isRenderedInDragClone;
  const dragState = useQueryBuilderDragState();
  const dropDisabled = isDropTargetDisabled(dragState, path, havingType);
  const dropForbidden = isDropTargetForbidden(dragState, havingType);

  const renderChild = (child: any, index: number, dragHandleProps?: DraggableProvidedDragHandleProps | null): React.ReactNode => {
    const ruleProps: IRuleProps<any> = {
      resultType,
      havingType,
      fields,
      types,
      value: child,
      isRoot: false,
      path: `${path}.${index}`,
      dragHandleProps,
    };

    return RuleFactory.componentFactory(index, ruleProps);
  };

  const rulesClassName = `groupRule__rules${rules.length > 1 ? ' groupRule__rules--multiple-rules' : ''}`;

  const containsDragSource = !!dragState && !!path && (dragState.sourcePath === path || dragState.sourcePath.startsWith(`${path}.`));

  const droppableClassName = (snapshot: DroppableStateSnapshot) => {
    const draggingOver = snapshot.isDraggingOver ? ' groupRule__rules--dragging-over' : '';
    const disabled = dropForbidden && !containsDragSource ? ' groupRule__rules--drop-disabled' : '';
    const emptyDropZone = rules.length === 0 && dragState && !dropForbidden ? ' groupRule__rules--empty-drop-zone' : '';
    return `${rulesClassName}${draggingOver}${disabled}${emptyDropZone}`;
  };

  const renderClone = (providedClone: DraggableProvided, snapshotClone: DraggableStateSnapshot, rubric: DraggableRubric) => (
    <DragCloneProvider value>
      <div ref={providedClone.innerRef} {...providedClone.draggableProps} className="queryBuilder__draggable-row queryBuilder__draggable-row--dragging">
        {renderChild(rules[rubric.source.index], rubric.source.index, providedClone.dragHandleProps)}
      </div>
    </DragCloneProvider>
  );

  const renderDraggableRules = () => (
    <Droppable droppableId={path as string} type="queryBuilderRules" isDropDisabled={dropDisabled} renderClone={renderClone}>
      {(provided: DroppableProvided, snapshot: DroppableStateSnapshot) => (
        <div ref={provided.innerRef} {...provided.droppableProps} className={droppableClassName(snapshot)}>
          {rules.map((x, index) => (
            <Draggable key={index} draggableId={`${path}.${index}`} index={index} isDragDisabled={isNestedRule(x)}>
              {(providedDraggable: DraggableProvided, snapshotDraggable: DraggableStateSnapshot) => (
                <div
                  ref={providedDraggable.innerRef}
                  {...providedDraggable.draggableProps}
                  className={`queryBuilder__draggable-row${snapshotDraggable.isDragging ? ' queryBuilder__draggable-row--dragging' : ''}`}
                >
                  {renderChild(x, index, providedDraggable.dragHandleProps)}
                </div>
              )}
            </Draggable>
          ))}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
  );

  return (
    <>
      {(rules.length !== 0 || canDragRules) && (
        <div className={`groupRule__actions-and-rules${rules.length === 0 ? ' groupRule__actions-and-rules--empty' : ''}`}>
          {rules.length > 1 && <ConditionSelector {...props} />}
          {canDragRules ? renderDraggableRules() : <div className={rulesClassName}>{rules.map((x, index) => renderChild(x, index))}</div>}
        </div>
      )}
    </>
  );
}
